import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import type { Restaurant, RestaurantGroup } from '@/types/api';
import { X, Copy, Check } from 'lucide-react';

interface RestaurantModalProps {
  restaurant: Restaurant | null;
  defaultGroupId?: number;
  onClose: () => void;
  onSuccess: () => void;
}

export default function RestaurantModal({ restaurant, defaultGroupId, onClose, onSuccess }: RestaurantModalProps) {
  const [formData, setFormData] = useState({
    groupId: restaurant?.groupId || defaultGroupId || ('' as number | ''),
    name: restaurant?.name || '',
    address: restaurant?.address || '',
    city: restaurant?.city || '',
    state: restaurant?.state || '',
    zipCode: restaurant?.zipCode || '',
    country: restaurant?.country || 'United Kingdom',
    phone: restaurant?.phone || '',
    email: restaurant?.email || '',
    website: restaurant?.website || '',
    timezone: restaurant?.timezone || 'Europe/London',
    currency: restaurant?.currency || 'GBP',
    maxTerminals: (restaurant?.maxTerminals ?? 10) as number | '',
    active: restaurant?.active ?? true,
  });

  // Snapshot of the form's initial values, to detect unsaved changes on close.
  const initialFormData = useRef(formData);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uuidCopied, setUuidCopied] = useState(false);

  const isDirty = JSON.stringify(formData) !== JSON.stringify(initialFormData.current);

  const handleClose = () => {
    if (isDirty && !window.confirm('Discard unsaved changes?')) {
      return;
    }
    onClose();
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isDirty]);

  const copyUuid = () => {
    if (!restaurant) return;
    navigator.clipboard.writeText(restaurant.uuid);
    setUuidCopied(true);
    setTimeout(() => setUuidCopied(false), 2000);
  };

  // Fetch available restaurant groups for the dropdown
  const { data: groups } = useQuery({
    queryKey: ['restaurant-groups'],
    queryFn: () => apiClient.getGroups(),
  });

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.createRestaurant(data as Partial<Restaurant>),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create restaurant';
      setErrors({ submit: message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.updateRestaurant(restaurant!.id, data as Partial<Restaurant>),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update restaurant';
      setErrors({ submit: message });
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.groupId) {
      newErrors.groupId = 'Restaurant group is required';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City/Town is required';
    }

    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'Postcode is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.maxTerminals === '' || formData.maxTerminals < 1) {
      newErrors.maxTerminals = 'Must allow at least 1 terminal';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Map frontend field names to backend DTO field names
  const toBackendPayload = () => ({
    groupId: formData.groupId,
    name: formData.name,
    addressLine1: formData.address,
    city: formData.city,
    state: formData.state,
    zipCode: formData.zipCode,
    country: formData.country,
    contactPhone: formData.phone,
    contactEmail: formData.email,
    website: formData.website,
    timezone: formData.timezone,
    currency: formData.currency,
    maxTerminals: formData.maxTerminals,
    active: formData.active,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const payload = toBackendPayload();
    if (restaurant) {
      await updateMutation.mutateAsync(payload);
    } else {
      await createMutation.mutateAsync(payload);
    }
  };

  const timezones = [
    'Europe/London',
    'Europe/Dublin',
    'Europe/Paris',
    'Europe/Berlin',
    'Europe/Madrid',
    'Europe/Rome',
    'Europe/Amsterdam',
    'Europe/Lisbon',
    'Europe/Athens',
    'Europe/Moscow',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Toronto',
    'America/Mexico_City',
    'America/Sao_Paulo',
    'Asia/Dubai',
    'Asia/Riyadh',
    'Asia/Kolkata',
    'Asia/Shanghai',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Asia/Hong_Kong',
    'Africa/Johannesburg',
    'Africa/Cairo',
    'Africa/Lagos',
    'Australia/Sydney',
    'Australia/Melbourne',
    'Pacific/Auckland',
  ];

  const currencies = [
    'GBP', 'EUR', 'USD', 'CAD', 'AUD', 'NZD',
    'AED', 'SAR', 'QAR', 'INR', 'CNY', 'SGD', 'JPY', 'HKD',
    'ZAR', 'NGN', 'EGP', 'MXN', 'BRL',
  ];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleClose}
    >
      <div
        className="card max-w-3xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {restaurant ? 'Edit Restaurant' : 'Create Restaurant'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {errors.submit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Restaurant UUID — read-only, needed to configure this restaurant on a POS terminal */}
          {restaurant && (
            <div className="border-b border-gray-200 pb-4">
              <label htmlFor="restaurant-uuid" className="block text-sm font-medium text-gray-700 mb-1">
                Restaurant UUID
              </label>
              <div className="flex gap-2">
                <input
                  id="restaurant-uuid"
                  type="text"
                  value={restaurant.uuid}
                  readOnly
                  className="input w-full bg-gray-50 text-gray-500 font-mono text-sm"
                />
                <button
                  type="button"
                  onClick={copyUuid}
                  className="btn btn-outline h-10 shrink-0 px-3"
                  title="Copy restaurant UUID"
                >
                  {uuidCopied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Use this UUID to connect this restaurant's POS terminals to the management server.
              </p>
            </div>
          )}

          {/* Restaurant Group */}
          <div className="border-b border-gray-200 pb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Restaurant Group</h3>
            <div>
              <label htmlFor="restaurant-group" className="block text-sm font-medium text-gray-700 mb-1">
                Group *
              </label>
              <select
                id="restaurant-group"
                value={formData.groupId}
                onChange={(e) => setFormData({ ...formData, groupId: e.target.value ? parseInt(e.target.value) : '' })}
                className={`input w-full ${errors.groupId ? 'border-red-500' : ''}`}
              >
                <option value="">Select a restaurant group...</option>
                {groups?.map((group: RestaurantGroup) => (
                  <option key={group.id} value={group.id}>{group.name}</option>
                ))}
              </select>
              {errors.groupId && <p className="text-red-500 text-xs mt-1">{errors.groupId}</p>}
              {(!groups || groups.length === 0) && (
                <p className="text-amber-600 text-xs mt-1">
                  No groups available. Create a restaurant group first from the Groups page.
                </p>
              )}
            </div>
          </div>

          {/* Basic Info */}
          <div className="border-b border-gray-200 pb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label htmlFor="restaurant-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Restaurant Name *
                </label>
                <input
                  id="restaurant-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`input w-full ${errors.name ? 'border-red-500' : ''}`}
                  placeholder="e.g., The Italian Kitchen"
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              <div>
                <label htmlFor="restaurant-phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone *
                </label>
                <input
                  id="restaurant-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className={`input w-full ${errors.phone ? 'border-red-500' : ''}`}
                  placeholder="020 7123 4567"
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>

              <div>
                <label htmlFor="restaurant-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="restaurant-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`input w-full ${errors.email ? 'border-red-500' : ''}`}
                  placeholder="contact@restaurant.com"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              <div className="md:col-span-2">
                <label htmlFor="restaurant-website" className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  id="restaurant-website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="input w-full"
                  placeholder="https://www.restaurant.com"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="border-b border-gray-200 pb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label htmlFor="restaurant-address" className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address *
                </label>
                <input
                  id="restaurant-address"
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className={`input w-full ${errors.address ? 'border-red-500' : ''}`}
                  placeholder="123 High Street"
                />
                {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
              </div>

              <div>
                <label htmlFor="restaurant-city" className="block text-sm font-medium text-gray-700 mb-1">
                  City / Town *
                </label>
                <input
                  id="restaurant-city"
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className={`input w-full ${errors.city ? 'border-red-500' : ''}`}
                  placeholder="London"
                />
                {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
              </div>

              <div>
                <label htmlFor="restaurant-state" className="block text-sm font-medium text-gray-700 mb-1">
                  County
                </label>
                <input
                  id="restaurant-state"
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className={`input w-full ${errors.state ? 'border-red-500' : ''}`}
                  placeholder="Greater London"
                />
                {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
              </div>

              <div>
                <label htmlFor="restaurant-zipcode" className="block text-sm font-medium text-gray-700 mb-1">
                  Postcode *
                </label>
                <input
                  id="restaurant-zipcode"
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                  className={`input w-full ${errors.zipCode ? 'border-red-500' : ''}`}
                  placeholder="SW1A 1AA"
                />
                {errors.zipCode && <p className="text-red-500 text-xs mt-1">{errors.zipCode}</p>}
              </div>

              <div>
                <label htmlFor="restaurant-country" className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  id="restaurant-country"
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="input w-full"
                  placeholder="United Kingdom"
                />
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className="border-b border-gray-200 pb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="restaurant-timezone" className="block text-sm font-medium text-gray-700 mb-1">
                  Timezone
                </label>
                <select
                  id="restaurant-timezone"
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  className="input w-full"
                >
                  {timezones.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="restaurant-currency" className="block text-sm font-medium text-gray-700 mb-1">
                  Currency
                </label>
                <select
                  id="restaurant-currency"
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="input w-full"
                >
                  {currencies.map((cur) => (
                    <option key={cur} value={cur}>{cur}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="restaurant-max-terminals" className="block text-sm font-medium text-gray-700 mb-1">
                  Max Terminals
                </label>
                <input
                  id="restaurant-max-terminals"
                  type="number"
                  min="1"
                  value={formData.maxTerminals}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxTerminals: e.target.value ? parseInt(e.target.value) : '',
                    })
                  }
                  className={`input w-full ${errors.maxTerminals ? 'border-red-500' : ''}`}
                />
                {errors.maxTerminals && <p className="text-red-500 text-xs mt-1">{errors.maxTerminals}</p>}
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="active"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="active" className="ml-2 text-sm text-gray-700">
              Active restaurant (visible in POS systems)
            </label>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="btn btn-outline flex-1 h-10">
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1 h-10"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : restaurant
                ? 'Update Restaurant'
                : 'Create Restaurant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
